import { FC, useState } from 'react'
import { Box, Collapse, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAzureApplicationOwnersAndDeputies } from 'hooks'
import { Skeleton } from '@mui/material'
import { PersonDetails } from './PersonDetails'
import { Pagination } from 'packages/eid-ui'
import { paginate } from 'utils'

const pageSize = 4

export type AzureApplicationOwnersProps = {
    className?: string
    buttonPosition?: 'right' | 'bottom'
    data: any
}

export const AzureApplicationOwners: FC<AzureApplicationOwnersProps> = (
    props,
) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [checked, setChecked] = useState(false)

    const handleChange = () => {
        setChecked((prev) => !prev)
    }

    const { data: ownersAndDeputies, isInitialLoading } =
        useAzureApplicationOwnersAndDeputies(props.data.id)

    const data = ownersAndDeputies && ownersAndDeputies.owners

    const [page, setPage] = useState(1)

    const owner = data && data.length > 0 && data[0]

    const paginatedData = data && paginate(data.slice(1), page, pageSize)

    const numberOfPages = data ? Math.ceil((data.length - 1) / pageSize) : 0

    const handlePageChange = (_: any, value: any) => {
        setPage(value)
    }

    if (isInitialLoading) {
        return (
            <Box paddingRight={4}>
                <Skeleton height={40} />
            </Box>
        )
    } else {
        if (data.length === 0) {
            return <Box paddingLeft={'4px'}>-</Box>
        }

        const showMoreCount = data && data.length - 1

        return (
            <Box
                sx={{
                    position: 'relative',
                }}
            >
                {data && owner && (
                    <Box
                        sx={{
                            ...(checked && {
                                opacity: 0.4,
                            }),
                        }}
                    >
                        <PersonDetails size="medium" person={owner} />
                    </Box>
                )}

                {checked && (
                    <Collapse in={checked} collapsedSize={0}>
                        <Box
                            sx={{
                                position: 'relative',
                                backgroundColor: theme?.palette?.grey?.[100],
                                borderRadius: '8px',
                                border: 'solid 1px #f9f9f9',
                                backdropFilter: 'blur(2px)',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: '150px',
                                    overflow: 'auto',
                                    padding: '5px 4px',
                                }}
                            >
                                {data &&
                                    paginatedData.map((o: any, index: any) => (
                                        <Box
                                            sx={{
                                                padding: '0px 12px',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    backgroundColor:
                                                        theme?.palette
                                                            ?.background?.paper,
                                                    '& p': {
                                                        color: theme?.palette
                                                            ?.common?.black,
                                                        fontWeight: 'bold',
                                                    },
                                                },
                                            }}
                                        >
                                            <PersonDetails
                                                key={o.id + index}
                                                size="small"
                                                person={o}
                                            />
                                        </Box>
                                    ))}
                            </Box>
                            {checked && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="100%"
                                    justifyContent="center"
                                    padding="5px 11px"
                                >
                                    <Pagination
                                        count={numberOfPages}
                                        size="small"
                                        siblingCount={0}
                                        page={page}
                                        onChange={handlePageChange}
                                        variant={'rounded'}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                )}

                {data && data.length > 1 && (
                    <Box
                        sx={{
                            paddingTop: theme.spacing(1.5),
                            '& > button': {
                                backgroundColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                color: theme?.palette?.primary?.main,
                                '&:hover': {
                                    cursor: 'pointer',
                                },
                            },
                        }}
                    >
                        <button onClick={handleChange}>
                            {checked
                                ? `- ${t('Common_ShowLess')}`
                                : `+ ${showMoreCount} ${t('Common_More')}`}
                        </button>
                    </Box>
                )}
            </Box>
        )
    }
}
